/**
 * Playtomic Booking Bridge — Deep Link Handoff Utility
 *
 * Constructs Playtomic booking URLs and handles cross-platform
 * deep linking (iOS Universal Links, Android App Links + Intent fallback, Desktop web).
 *
 * URL format matches Playtomic's own share pattern, which is what their
 * iOS Universal Link / Android App Link handlers are configured to recognize:
 *   https://playtomic.io/tenant/{tenantId}?utm_campaign=share&utm_source=app_ios
 */

import { Browser } from '@capacitor/browser';

export interface PlaytomicHandoffParams {
  slug: string;
  tenantId: string;
  /** ISO date string, e.g. "2026-04-10" — accepted for API compatibility, not embedded in URL */
  date?: string;
  /** Time string, e.g. "18:00" — accepted for API compatibility, not embedded in URL */
  time?: string;
  /** Duration in minutes — accepted for API compatibility, not embedded in URL */
  durationMinutes?: number;
  /** UTM medium for tracking */
  utmMedium?: string;
}

type Platform = 'ios' | 'android_chrome' | 'android_other' | 'desktop';

const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();

  const isIOS = /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';

  const isAndroid = /android/.test(ua);
  if (isAndroid) {
    const isChrome = /chrome/.test(ua) && !/edge/.test(ua);
    return isChrome ? 'android_chrome' : 'android_other';
  }

  return 'desktop';
};

const utmSourceForPlatform = (platform: Platform): string => {
  switch (platform) {
    case 'ios':
      return 'app_ios';
    case 'android_chrome':
    case 'android_other':
      return 'app_android';
    default:
      return 'luvall_web';
  }
};

/**
 * Build the Playtomic web booking URL using the share pattern that
 * Playtomic's mobile apps register as a Universal Link / App Link.
 */
export const buildPlaytomicUrl = (
  params: PlaytomicHandoffParams,
  platform?: Platform,
): string => {
  const { tenantId, utmMedium = 'match_card' } = params;
  const resolved = platform ?? detectPlatform();

  const url = new URL(`https://playtomic.io/tenant/${tenantId}`);
  url.searchParams.set('utm_campaign', 'share');
  url.searchParams.set('utm_source', utmSourceForPlatform(resolved));
  url.searchParams.set('utm_medium', utmMedium);

  return url.toString();
};

/**
 * Open Playtomic booking using the best available method for the current platform.
 *
 * - Capacitor native: open in system browser (lets iOS/Android route to Playtomic app via Universal/App Link)
 * - iOS web: same-origin redirect (iOS handles Universal Link → app, falls back to Safari)
 * - Android Chrome: open web URL → 400 ms timeout → intent:// fallback targeting com.playtomic.app
 * - Android Firefox / desktop: new tab
 */
export const openPlaytomicBooking = async (params: PlaytomicHandoffParams): Promise<void> => {
  const platform = detectPlatform();
  const webUrl = buildPlaytomicUrl(params, platform);

  // If running inside a Capacitor native app, open in the system browser
  // so iOS Universal Links / Android App Links can trigger the Playtomic app.
  const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
  if (isNative) {
    await Browser.open({ url: webUrl });
    return;
  }

  switch (platform) {
    case 'ios':
      window.location.href = webUrl;
      break;

    case 'android_chrome': {
      const opened = window.open(webUrl, '_blank');
      let didLeave = false;
      const onVisibility = () => {
        if (document.hidden) didLeave = true;
      };
      document.addEventListener('visibilitychange', onVisibility);

      setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisibility);
        if (!didLeave && !opened) {
          // Intent URI mirrors the same tenant share path so Playtomic's
          // Android App Link intent filter matches.
          const intentUrl =
            `intent://tenant/${encodeURIComponent(params.tenantId)}` +
            `?utm_campaign=share&utm_source=app_android` +
            `#Intent;scheme=https;package=com.playtomic.app;` +
            `S.browser_fallback_url=${encodeURIComponent(webUrl)};end`;
          window.location.href = intentUrl;
        }
      }, 400);
      break;
    }

    case 'android_other':
    case 'desktop':
    default:
      window.open(webUrl, '_blank', 'noopener,noreferrer');
      break;
  }
};
